import React, { useEffect, useState } from "react";
import PhoneCallbackIcon from "@mui/icons-material/PhoneCallback";
import PhoneForwardedIcon from "@mui/icons-material/PhoneForwarded";
import PhoneMissedIcon from "@mui/icons-material/PhoneMissed";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { getFetch } from "../../../../../../fetch/fetch";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "./callhistory.module.css";
import JourneyList from "./journeylist";
import { formatHm, formatTalk, parseLocal, formatRoomTime } from "./callhistoryformat";
import { formatDuration } from "../phoneinbound/receiverrow";

const toastOptions : any = {
    position: "bottom-right",
    autoClose: 3000,
    hideProgressBar: true,
    theme: "colored"
};

const MISSED = ["abandoned", "timeout", "empty"];

//통화 1건: [원형 아이콘] [제목/메타(시각 포함)] [▶ 📄] + 펼침(여정) + 오디오 + 요약 한 줄 + 수동처리 메모
const CallRow : React.FC<any> = (props) => {
    const call = props.call;
    const [expanded, setExpanded] = useState<boolean>(false);
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [audioLoading, setAudioLoading] = useState<boolean>(false);

    //행이 바뀌면(다른 고객 선택) 재생 상태 초기화
    useEffect(() => {
        setAudioUrl(null);
        setExpanded(false);
    }, [call.callId]);

    const inbound = call.direction === "inbound";
    const missed = inbound && MISSED.indexOf(call.result) !== -1;
    const inProgress = call.result === "in_progress";
    const connected = call.result === "connected" || call.result === "outbound_connected";
    const outboundNoanswer = call.result === "outbound_noanswer";

    //원형 틴트: 통화중 파랑 / 부재중 빨강 / 인바운드 연결 초록 / 발신·기타 베이지
    const tileClass = inProgress ? styles.tileBlue
        : missed ? styles.tileRed
        : (inbound && connected) ? styles.tileGreen
        : styles.tileGray;
    const tileIcon = inProgress ? <PhoneInTalkIcon sx={{ fontSize: 18 }} />
        : missed ? <PhoneMissedIcon sx={{ fontSize: 17 }} />
        : inbound ? <PhoneCallbackIcon sx={{ fontSize: 18 }} />
        : <PhoneForwardedIcon sx={{ fontSize: 18 }} />;
    const queueLabel = call.queueName ? `${call.queueName} 큐` : (call.queue ? `${call.queue} 큐` : "큐");

    let title : React.ReactNode;
    let meta = "";
    if (inbound) {
        if (inProgress) {
            const since = parseLocal(call.connectedAt || call.startedAt);
            const sec = since === null ? 0 : Math.max(0, Math.floor((props.now - since) / 1000));
            title = <span className={styles.callTitleIncall}>통화중 · {call.agentName || "상담원"}</span>;
            meta = `${queueLabel} · 대기 ${call.waitSec ?? 0}초 · 통화 ${formatDuration(sec)}`;
        } else if (missed) {
            const label = call.result === "timeout" ? "대기 시간 초과" : call.result === "empty" ? "상담원 없음" : "부재중";
            title = <span className={styles.callTitleMissed}>{label}</span>;
            meta = `${queueLabel} · ${call.waitSec ?? 0}초 대기 후 종료`;
        } else if (connected) {
            title = <span className={styles.callTitleStrong}>연결 · {call.agentName || "상담원"}</span>;
            meta = `${queueLabel} · 대기 ${call.waitSec ?? 0}초 · 통화 ${formatTalk(call.talkSec)}`;
        } else if (call.result === "waiting") {
            title = <span className={styles.callTitleIncall}>대기중</span>;
            meta = `${queueLabel} · 대기 중`;
        } else {
            title = <span className={styles.callTitleMuted}>기록 불완전</span>;
            meta = queueLabel;
        }
    } else {
        if (connected) {
            title = <span className={styles.callTitleStrong}>발신 · {call.agentName || "상담원"}</span>;
            meta = `상담원 → 고객 · 내선 ${call.extension || "?"} · 통화 ${formatTalk(call.talkSec)}`;
        } else {
            title = <span className={styles.callTitleMuted}>발신 · 응답 없음</span>;
            meta = `${call.agentName || "상담원"} → 고객 · 내선 ${call.extension || "?"}`;
        }
    }

    const toggleAudio = async (e : React.MouseEvent) => {
        e.stopPropagation();
        if (audioUrl) {
            setAudioUrl(null);
            return;
        }
        setAudioLoading(true);
        try {
            //presigned URL 은 클릭 시점에 발급, 컴포넌트 state 에만 보관
            const result : any = await getFetch(`/dashboard/callcenter/history/calls/recordingUrl?callId=${encodeURIComponent(call.callId)}`);
            if (result.message === "success" && result.url) {
                setAudioUrl(result.url);
            } else {
                toast.error(result.message === "noRecording" ? "녹음 파일이 아직 없습니다" : "녹음을 불러오지 못했습니다", toastOptions);
            }
        } catch (err) {
            console.log(err);
            toast.error("녹음을 불러오지 못했습니다", toastOptions);
        }
        setAudioLoading(false);
    };

    const toggleExpand = () => {
        const next = !expanded;
        setExpanded(next);
        if (next) {
            props.onLoadJourney(call);
        }
    };

    const summary = call.summary;
    const memo = call.manualDoneReason;

    return (
        <div className={styles.callRow}>
            <div className={styles.callMain}>
                <div className={styles.callClickable} onClick={toggleExpand}>
                    <div className={`${styles.iconTile} ${tileClass}`}>
                        {tileIcon}
                    </div>
                    <div className={styles.callText}>
                        <div className={styles.callTitle}>{title}</div>
                        <div className={styles.callMeta}>{meta} · {formatHm(call.startedAt)}</div>
                    </div>
                </div>
                <div className={styles.callActions}>
                    {
                        call.hasRecording ?
                            <button className={`${styles.iconBtn} ${audioUrl ? styles.iconBtnActive : ""}`} onClick={toggleAudio} title={audioUrl ? "재생 닫기" : "녹음 재생"} disabled={audioLoading}>
                                {audioUrl ? <StopIcon sx={{ fontSize: 17 }} /> : <PlayArrowIcon sx={{ fontSize: 18 }} />}
                            </button>
                            : null
                    }
                    {
                        call.hasTranscript ?
                            <button className={styles.iconBtn} onClick={(e) => { e.stopPropagation(); props.onTranscript(call); }} title="전문 보기">
                                <DescriptionOutlinedIcon sx={{ fontSize: 17 }} />
                            </button>
                            : null
                    }
                </div>
            </div>
            {
                audioUrl ?
                    <div className={styles.audioBox}>
                        <audio controls autoPlay src={audioUrl} />
                    </div>
                    : null
            }
            {
                summary && summary.topic ?
                    <div className={styles.summaryLine}>
                        <span className={styles.summaryTopic}>요약: {summary.topic}</span>
                        {Array.isArray(summary.points) && summary.points.length ? <span> — {summary.points.join(" ")}</span> : ""}
                        {summary.nextAction ? <span className={styles.summaryNext}> {summary.nextAction}.</span> : ""}
                        {call.hasTranscript ? <button className={styles.summaryLink} onClick={(e) => { e.stopPropagation(); props.onTranscript(call); }}>전문 보기</button> : ""}
                    </div>
                    : null
            }
            {
                memo ?
                    <div className={styles.memoBox}>
                        <div className={styles.memoHead}>수동 처리 · {call.manualDoneBy || ""} · {formatRoomTime(call.manualDoneAt || "")}</div>
                        <div>{memo}</div>
                    </div>
                    : null
            }
            {
                expanded ?
                    <JourneyList
                        loading={props.journey && props.journey.loading}
                        error={props.journey && props.journey.error}
                        events={props.journey && props.journey.events}
                        direction={call.direction}
                    />
                    : null
            }
        </div>
    );
};

export default CallRow;
